import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Calendar, CheckCircle } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { app } from '../firebase';
import { getFirestore, doc, updateDoc, getDoc } from 'firebase/firestore';
import { useUserProfile } from '../contexts/nobishiro-dashboard/useUserProfile';

const GoalSettingPage = () => {
  const { user } = useAuth();
  const { profile, loading } = useUserProfile(user?.uid);
  const navigate = useNavigate();
  const db = getFirestore(app);

  const [goal, setGoal] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    if (profile?.talentPortfolio?.growthGoals) {
      const activeGoal = profile.talentPortfolio.growthGoals.find(g => g.status === 'in-progress');
      if (activeGoal) {
        setGoal(activeGoal.text || '');
      }
    }
  }, [profile]);

  const isFormComplete = goal.trim() !== '';

  const handleSubmit = async () => {
    if (!isFormComplete || !user) return;
    setIsSaving(true);
    
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      const currentData = userDoc.data();
      
      const newGoal = {
        id: `goal_${Date.now()}`,
        text: goal,
        status: 'in-progress',
        createdAt: new Date().toISOString()
      };

      const existingGoals = currentData.talentPortfolio?.growthGoals || [];
      const activeGoalIndex = existingGoals.findIndex(g => g.status === 'in-progress');

      let updatedGoals;
      if (activeGoalIndex > -1) {
        updatedGoals = [...existingGoals];
        updatedGoals[activeGoalIndex] = { ...updatedGoals[activeGoalIndex], text: goal };
      } else {
        updatedGoals = [...existingGoals, newGoal];
      }

      await updateDoc(userDocRef, {
        'talentPortfolio.growthGoals': updatedGoals
      });

      setIsSaved(true);
      setTimeout(() => {
        setIsSaved(false);
        navigate('/dashboard');
      }, 2000);

    } catch (error) {
      console.error("目標の保存に失敗:", error);
      alert("エラーが発生しました。もう一度お試しください。");
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center">読み込み中...</div>;
  }

  return (
    <div className="w-full max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
              <Target className="w-12 h-12 text-blue-600 mx-auto mb-2" />
              <h1 className="text-2xl font-bold text-gray-900">成長目標の設定</h1>
              <p className="text-gray-500 mt-1">現在最も注力している成長目標を入力してください。</p>
          </div>
          <div className="space-y-6 bg-white p-8 rounded-lg shadow-sm border">
              <div>
                  <label htmlFor="goal-text" className="block text-lg font-semibold text-gray-700 mb-2">達成したい目標</label>
                  <textarea id="goal-text" value={goal} onChange={(e) => setGoal(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="例：新しいマーケティングスキルを習得し、プロジェクトを成功させる"></textarea>
              </div>
          </div>
          <div className="mt-8 text-center">
              <button onClick={handleSubmit} disabled={!isFormComplete || isSaving} className="px-10 py-4 bg-blue-600 text-white rounded-lg font-bold text-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed">
                  {isSaving ? '保存中...' : '目標を保存・更新する'}
              </button>
              {isSaved && <p className="text-green-600 mt-4 flex items-center justify-center gap-2"><CheckCircle/>保存しました！ダッシュボードに戻ります...</p>}
          </div>
      </div>
    </div>
  );
};

export default GoalSettingPage;